if (typeof Header !== 'function') {
	class Header extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				header: 'header',
				headerWrapper: '.header__wrapper',
				headerPlaceholder: '.header-placeholder',
				headerObserver: '.header-observer',
				burgerMenuButton: '.burger-menu__button',
				headerMenu: '.header-menu',
				headerMenuNav: '.header-menu__nav',
				headerMenuNavPrimary: '.header-menu__nav--primary',
				headerMenuItem: '.header-menu__item',
				headerUpsell: '.header-menu__upsell',
				headerUpsellList: '.header-upsell__list',
				headerUpsellListItem: '.header-upsell__list-item',
				headerSidebarExtraTrigger: '.header-sidebar__icon--extra-trigger',
				headerSidebarCloseIcon: '.header-sidebar__icon--close',
			};

			this.classes = {
				sticky: 'header--sticky',
				stickyActive: 'header--sticky-active',
				stickyActiveHidden: 'header--sticky-active-hidden',
				stickyActiveVisible: 'header--sticky-active-visible',
				headerMenuOpen: 'header-menu--open',
				headerMenuNavVisible: 'header-menu__nav--visible',
				headerMenuItemReturn: 'header-menu__item--return',
				headerMenuItemReturnIcon: 'header-menu__return-icon',
				headerUpsellVisible: 'header-menu__upsell--visible',
				headerUpsellPortrait: 'header-menu__upsell--ratio-4-3',
				headerSidebarExtra: 'header-sidebar__extra',
				headerSidebarExtraOpen: 'header-sidebar__extra--open',
				blockedScroll: 'blocked-scroll',
			};

			// Elements
			this.body = document.querySelector('body');
			this.header = this.querySelector(this.selectors.header);
			this.headerWrapper = this.querySelector(this.selectors.headerWrapper);
			this.headerPlaceholder = this.querySelector(this.selectors.headerPlaceholder);
			this.headerObserver = document.querySelector(this.selectors.headerObserver);
			this.burgerMenuButton = this.querySelector(this.selectors.burgerMenuButton);
			this.headerMenu = this.querySelector(this.selectors.headerMenu);
			this.headerSidebarCloseIcon = this.querySelector(this.selectors.headerSidebarCloseIcon);
			this.headerUpsellSplide = null;

			// Variables
			this.headerHeight = this.headerWrapper.offsetHeight;
			this.isMobile = window.innerWidth < 768;

			// Flags
			this.headerIsSticky = this.header.classList.contains(this.classes.sticky);
			this.headerUpsellActive = this.dataset.upsellActive === 'true';

			// Methods
			this.init();
		}

		init() {
			this.headerIsSticky && this.stickyHeaderSetup();
			this.headerUpsellActive && this.setupUpsell();

			this.addEventListeners();
			this.setInformativeHeaderHeight();
		}

		setInformativeHeaderHeight(reset) {
			const { body, headerHeight, headerIsSticky } = this;

			const headerHeightAdjusted = headerIsSticky ? headerHeight : 0;

			body.style.setProperty('--menu-sticky-height', `${reset ? 0 : headerHeightAdjusted}px`);
		}

		addEventListeners() {
			this.burgerMenuButton.addEventListener('click', this.burgerMenuToggle.bind(this));
			this.headerSidebarCloseIcon.addEventListener('click', this.burgerMenuToggle.bind(this));
			this.querySelectorAll(this.selectors.headerMenuItem).forEach((child) => {
				child.addEventListener('click', this.openNavigationMenu.bind(this));
			});

			this.querySelectorAll(this.selectors.headerSidebarExtraTrigger).forEach((child) => {
				child.addEventListener('click', this.toggleSidebarExtraMenu.bind(this, child.dataset.trigger));
			});

			this.body.addEventListener('keydown', (event) => {
				if (
					this.header.classList.contains(this.classes.headerMenuOpen) &&
					(event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27)
				) {
					this.burgerMenuToggle();
				}
			});
		}

		toggleSidebarExtraMenu(trigger) {
			const { classes } = this;
			const sidebarExtraMenu = this.header.querySelector(`.${this.classes.headerSidebarExtra}--${trigger}`);

			this.header.querySelectorAll(`.${classes.headerSidebarExtra}`).forEach((child) => {
				if (child !== sidebarExtraMenu) {
					child.classList.remove(classes.headerSidebarExtraOpen);
				}
			});

			if (sidebarExtraMenu.classList.contains(classes.headerSidebarExtraOpen)) {
				sidebarExtraMenu.classList.remove(classes.headerSidebarExtraOpen);
			} else {
				sidebarExtraMenu.classList.add(classes.headerSidebarExtraOpen);
			}
		}

		setupUpsell() {
			const { selectors, isMobile, header, headerMenu } = this;
			const headerUpsell = this.querySelector(selectors.headerUpsell);
			const headerUpsellList = this.querySelector(selectors.headerUpsellList);
			const headerUpsellListItems = [...headerUpsellList.children];
			const isPortrait = headerUpsell.classList.contains(this.classes.headerUpsellPortrait);
			const listItemWidth = isMobile ? (isPortrait ? 200 : 150) : isPortrait ? 266 : 200;
			const repeatCount = Math.ceil(window.innerWidth / listItemWidth / headerUpsellList.childElementCount);
			const headerUpsellClones = Array(repeatCount).fill(headerUpsellListItems).flat();
			const headerVerticalSpacing = +headerMenu.style
				.getPropertyValue('--nav-vertical-spacing')
				.replace('px', '');

			headerUpsellList.innerHTML = '';
			headerUpsellClones.forEach((item) => headerUpsellList.appendChild(item.cloneNode(true)));
			header.style.setProperty(
				'--padding-bottom-upsell',
				`${headerUpsell.clientHeight + headerVerticalSpacing}px`
			);

			this.headerUpsellSplide = new Splide(headerUpsell, {
				type: 'loop',
				arrows: false,
				pagination: false,
				autoWidth: true,
				drag: 'free',
				focus: 'center',
				autoScroll: {
					speed: isMobile ? 0.2 : 0.5,
					autoStart: false,
				},
			});

			this.headerUpsellSplide.mount(window.splide.Extensions);
		}

		burgerMenuToggle() {

			const { body, header, headerMenu, headerHeight, classes, selectors, headerUpsellActive, headerWrapper } =
				this;
			const headerMenuExpanded = header.classList.contains(classes.headerMenuOpen);

			if (headerMenuExpanded) {
				if (headerUpsellActive) {
					this.headerUpsellSplide.Components.AutoScroll.pause();
					this.querySelector(selectors.headerUpsell).classList.remove(classes.headerUpsellVisible);
				}

				this.header.querySelectorAll(`.${this.classes.headerSidebarExtra}`).forEach((child) => {
					child.classList.remove(this.classes.headerSidebarExtraOpen);
				});

				setTimeout(() => {
					headerMenu.style.maxHeight = 0;

					this.querySelectorAll(selectors.headerMenuNav).forEach((child) => {
						child.classList.remove(classes.headerMenuNavVisible);
					});
					body.classList.remove(classes.blockedScroll);
					header.classList.remove(classes.headerMenuOpen);
					headerMenu.classList.remove(classes.headerMenuOpen);
					headerWrapper.focus();
				},50);
			} else {
				headerMenu.style.maxHeight = `${document.documentElement.clientHeight}px`;
				this.querySelector(selectors.headerMenuNavPrimary).classList.add(classes.headerMenuNavVisible);
				body.classList.add(classes.blockedScroll);
				header.classList.add(classes.headerMenuOpen);
				headerMenu.classList.add(classes.headerMenuOpen);

				setTimeout(() => {
					if (headerUpsellActive) {
						this.querySelector(selectors.headerUpsell).classList.add(classes.headerUpsellVisible);
						this.headerUpsellSplide.Components.AutoScroll.play();
					}
				}, 400);
			}
		}

		stickyHeaderSetup() {
			const { header, headerPlaceholder, headerObserver, classes, headerHeight } = this;
			const headerOffsetTop = headerPlaceholder.offsetTop;

			const options = {
				rootMargin: `${headerOffsetTop}px 0px 0px 0px`,
				root: null,
			};

			const optionsSecondary = {
				rootMargin: `${headerOffsetTop + 100}px 0px 0px 0px`,
				root: null,
			};

			const observerCallback = (entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						headerPlaceholder.style.height = 0;
						header.classList.remove(classes.stickyActive);
					} else {
						headerPlaceholder.style.height = `${headerHeight}px`;
						header.classList.add(classes.stickyActive);
					}
				});
			};

			const observerCallbackSecondary = (entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						header.classList.remove(classes.stickyActiveVisible);
						this.setInformativeHeaderHeight(true);
					} else {
						header.classList.add(classes.stickyActiveVisible);
						this.setInformativeHeaderHeight();
					}
				});
			};

			const observer = new IntersectionObserver(observerCallback, options);
			const observerSecondary = new IntersectionObserver(observerCallbackSecondary, optionsSecondary);

			observer.observe(headerObserver);
			observerSecondary.observe(headerObserver);
		}

		openNavigationMenu(e) {
			const { classes, selectors, headerMenu } = this;
			const isRedirect = e.currentTarget.tagName === 'A' || e.target.tagName === 'A';
			const isReturn = e.currentTarget.classList.contains(classes.headerMenuItemReturn);
			const currentParent = e.currentTarget.closest(selectors.headerMenuNav);

			currentParent.style.zIndex = 1;

			if (isRedirect) {
				return;
			}

			if (isReturn) {
				e.preventDefault();
				currentParent.parentElement.parentElement.classList.add(classes.headerMenuNavVisible);
			} else {
				const nextVisible = e.currentTarget.nextElementSibling;

				nextVisible.classList.add(classes.headerMenuNavVisible);

				if (nextVisible.dataset.level === '3') {
					e.currentTarget.closest(selectors.headerMenuNav).style.zIndex = 2;
				}
			}

			e.currentTarget.closest(selectors.headerMenuNav).classList.remove(classes.headerMenuNavVisible);
			headerMenu.scrollTop = 0;

			for (const child of this.querySelectorAll(selectors.headerMenuNav)) {
				child.scrollTop = 0;
			}
		}
	}

	if (!customElements.get('theme-header')) {
		customElements.define('theme-header', Header);
	}
}
